import HttpClient from '@/util/http/HttpClient';
import Page from '@/repositories/data/Page';
import ApiWrapper from '@/repositories/data/ApiWrapper';
import { from, Observable, throwError } from 'rxjs';
import { catchError, flatMap, map, startWith } from 'rxjs/operators';
import FormModel from '@/repositories/data/FormModel';
import Resource from '@/repositories/Resource';

export function unwrapApiWrapper<T>(observable: Observable<ApiWrapper<T>>): Observable<T | null> {
    return observable.pipe(
        flatMap((wrapper) => (wrapper.success ? from([wrapper.data]) : throwError(new Error('Remote server failure')))),
    );
}

export function networkBoundResource<T>(observable: Observable<T | null>): Observable<Resource<T>> {
    return observable.pipe(
        map((t) => Resource.success(t)),
        catchError((t) => from([Resource.error<T>(t, null)])),
        startWith(Resource.loading<T>(null)),
    );
}

export enum SortDirection {
    ASC = 'ASC',
    DESC = 'DESC',
}

export interface Sort {
    property: string;
    direction: SortDirection;
}

export interface Pageable {
    page: number;
    size: number;
    sort: Sort[];
    filter?: any;
}

export default abstract class BaseCrudRepository<I, T, F> {
    protected readonly client: HttpClient;
    protected readonly baseUrl: string;

    protected constructor(client: HttpClient, basePath: string) {
        this.client = client;
        this.baseUrl = process.env.VUE_APP_API_ENDPOINT + '/api/' + basePath;
    }

    public findPage(pageable: Pageable): Observable<Resource<Page<T>>> {
        const params: any = {
            page: pageable.page,
            size: pageable.size,
            sort: pageable.sort.map((t) => t.property + ',' + t.direction).join(','),
        };
        if (pageable.filter) {
            params.q = pageable.filter;
        }
        const observable = this.client.get<ApiWrapper<Page<T>>>('', {
            baseUrl: this.baseUrl,
            params,
        });
        return networkBoundResource(unwrapApiWrapper(observable));
    }

    public findOne(id: I): Observable<Resource<T>> {
        const observable = this.client.get<ApiWrapper<T>>(this.formatId(id), { baseUrl: this.baseUrl });
        return networkBoundResource(unwrapApiWrapper(observable));
    }

    public create(entity: F): Observable<Resource<T>> {
        entity = this.processBeforeCreate(entity);
        const observable = this.client.post<ApiWrapper<T>>('', entity, { baseUrl: this.baseUrl });
        return networkBoundResource(unwrapApiWrapper(observable));
    }

    public update(entity: F): Observable<Resource<T>> {
        entity = this.processBeforeUpdate(entity);
        const observable = this.client.put<ApiWrapper<T>>('', entity, { baseUrl: this.baseUrl });
        return networkBoundResource(unwrapApiWrapper(observable));
    }

    public delete(id: I): Observable<Resource<void>> {
        const observable = this.client.delete<ApiWrapper<any>>(this.formatId(id), { baseUrl: this.baseUrl });
        return networkBoundResource(unwrapApiWrapper(observable));
    }

    public introspect(): Observable<Resource<FormModel>> {
        const observable = this.client.get<ApiWrapper<any[]>>('introspect', { baseUrl: this.baseUrl });
        return networkBoundResource(unwrapApiWrapper(observable).pipe(map((t) => new FormModel(t!))));
    }

    public findRelated(id: I, relationship: string): Observable<Resource<any[]>> {
        let url: string;
        if (typeof id === 'object') {
            url = 'relationships/' + relationship + this.formatId(id);
        } else {
            url = this.formatId(id) + '/relationships/' + relationship;
        }
        const observable = this.client.get<ApiWrapper<any[]>>(url, {
            baseUrl: this.baseUrl,
        });
        return networkBoundResource(unwrapApiWrapper(observable));
    }

    public putRelation(id: I, relationship: string, inputForm: object): Observable<Resource<T>> {
        let url: string;
        if (typeof id === 'object') {
            url = 'relationships/' + relationship + this.formatId(id);
        } else {
            url = this.formatId(id) + '/relationships/' + relationship;
        }
        const observable = this.client.put<ApiWrapper<T>>(url, inputForm, { baseUrl: this.baseUrl });
        return networkBoundResource(unwrapApiWrapper(observable));
    }

    protected formatId(id: I): string {
        if (typeof id === 'number' || typeof id === 'string') {
            return '' + id;
        } else if (typeof id === 'object') {
            let idFormat = '?';
            const idObject: { [key: string]: any[] } = <{ [key: string]: any[] }>(<unknown>id);
            for (const key in idObject) {
                if (Object.prototype.hasOwnProperty.call(idObject, key)) {
                    idFormat += key.toString() + '=' + idObject[key].toString() + '&';
                }
            }
            return idFormat;
        } else {
            return '' + id;
        }
    }

    protected processBeforeCreate(entity: F): F {
        return entity;
    }

    protected processBeforeUpdate(entity: F): F {
        return entity;
    }
}
